<template>
  <div class="vacancy-card">
    <div class="vacancy-card__inner">
      <div class="vacancy-card__date">
        <BasicDate
          :date="vacancy.publishedDate.date"
          :title="vacancy.publishedDate.title"
        />
      </div>

      <a :href="vacancy.href" class="vacancy-card__icon-link">
        <img
          :src="vacancy.company.logo.src"
          :alt="`Логотип компании «${vacancy.company.title}»`"
          class="vacancy-card__icon"
        />
      </a>

      <div class="vacancy-card__info">
        <div class="vacancy-card__company">
          <div class="vacancy-card__company-title">
            <BasicLink
              :title="vacancy.company.title"
              :href="vacancy.company.href"
              appearance="dark"
            />
          </div>
          <CompanyIcons :company="vacancy.company" />
          <div
            v-if="vacancy.company.rating"
            class="vacancy-card__company-rating"
          >
            <CompanyRating :rating="vacancy.company.rating" />
          </div>
        </div>

        <div class="vacancy-card__title">
          <a
            class="vacancy-card__title-link"
            :href="vacancy.href"
            v-text="vacancy.title"
          />
        </div>

        <div class="vacancy-card__meta">
          <VacancyMeta :vacancy="vacancy" />
        </div>

        <div v-if="vacancy.salary" class="vacancy-card__salary">
          <BasicSalary :salary="vacancy.salary" />
        </div>

        <div class="vacancy-card__skills">
          <VacancySkills :vacancy="vacancy" />
        </div>

        <div v-if="vacancy.media" class="vacancy-card__media">
          <LiteYoutubeEmbed v-if="youtubeEmbed" v-bind="youtubeEmbed" />
          <iframe
            v-else-if="vacancy.media"
            class="vacancy-card__iframe"
            :src="vacancy.media.src"
            allowfullscreen
            width="350"
            height="197"
          />
        </div>
      </div>

      <div class="vacancy-card__footer">
        <div v-if="vacancy.reactions" class="vacancy-card__reactions">
          <ResourceAction
            :action="reactionAction"
            :params="{ vacancyId: vacancy.id }"
            ref="reactionAction"
          >
            <ReactionsPanel
              v-model="vacancy.reactions.items"
              :fallback-href="vacancy.reactions.fallbackHref"
              @update:modelValue="syncReactions"
            />
          </ResourceAction>
        </div>

        <div class="vacancy-card__actions">
          <a
            v-if="withEditLink"
            class="vacancy-card__edit"
            title="Редактировать"
            :href="vacancyEditUrl"
          >
            <SvgIcon icon="edit_new" :size="24" />
          </a>

          <div v-if="vacancy.response" class="vacancy-card__response">
            <VacancyResponseFromCard
              :kind="vacancy.response.kind"
              :vacancy-id="vacancy.id"
            />
          </div>

          <ResourceAction
            v-if="!vacancy.archived"
            :action="
              !vacancy.favorite
                ? deleteFromFavoritesAction
                : addToFavoritesAction
            "
            :params="{ vacancyId: vacancy.id }"
            ref="favoritesAction"
            v-slot="{ isLoading }"
            @success="onFavoritesChangeSucccess"
          >
            <VacancyFavoriteBtn
              @update:favorite="syncFavorite"
              v-model="vacancy.favorite"
              :disabled="isLoading"
            />
          </ResourceAction>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  VacanciesFavoritesResource,
  VacanciesReactionsResource,
} from 'src/services/vacancies.js';

import BasicDate from 'src/components/BasicDate/BasicDate.vue';
import BasicLink from 'src/components/BasicLink/BasicLink.vue';
import BasicSalary from 'src/components/BasicSalary/BasicSalary.vue';
import CompanyIcons from 'src/components/companies/CompanyIcons/CompanyIcons.vue';
import SvgIcon from 'src/components/icons/SvgIcon/SvgIcon.vue';
import ReactionsPanel from 'src/components/reactions/ReactionsPanel/ReactionsPanel.vue';
import ResourceAction from 'src/components/ResourceAction/ResourceAction.vue';
import CompanyRating from 'src/components/resume/CompanyRating/CompanyRating.vue';
import VacancyFavoriteBtn from 'src/components/vacancies/VacancyFavoriteBtn/VacancyFavoriteBtn.vue';
import VacancyMeta from 'src/components/vacancies/VacancyMeta/VacancyMeta.vue';
import VacancyResponseFromCard from 'src/components/vacancies/VacancyResponseFromCard/VacancyResponseFromCard.vue';
import VacancySkills from 'src/components/vacancies/VacancySkills/VacancySkills.vue';

const LiteYoutubeEmbed = () =>
  import('src/components/embed/LiteYoutubeEmbed/LiteYoutubeEmbed.vue');

export default {
  name: 'VacancyCard',
  components: {
    CompanyIcons,
    SvgIcon,
    VacancyFavoriteBtn,
    VacancySkills,
    VacancyMeta,
    LiteYoutubeEmbed,
    CompanyRating,
    ResourceAction,
    ReactionsPanel,
    BasicLink,
    BasicDate,
    BasicSalary,
    VacancyResponseFromCard,
  },
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    withEditLink: Boolean,
  },
  methods: {
    reactionAction: VacanciesReactionsResource.post,
    addToFavoritesAction: VacanciesFavoritesResource.post,
    deleteFromFavoritesAction: VacanciesFavoritesResource.delete,
    syncFavorite() {
      this.$refs.favoritesAction.dispatch();
    },
    syncReactions(reactions) {
      const newReactions = reactions
        .filter((reaction) => reaction.hasReacted)
        .map((reaction) => reaction.name);
      this.$refs.reactionAction.dispatch({ reactions: newReactions });
    },
    onFavoritesChangeSucccess({ data }) {
      if (data && data.redirect) {
        window.location = data.redirect;
      }
    },
  },
  computed: {
    vacancyEditUrl() {
      return `/companies/${this.vacancy.company.alias_name}/cp/vacancies/${this.vacancy.id}/edit`;
    },
    youtubeEmbed() {
      // eslint-disable-next-line prefer-const
      let { media: { src, title } = {} } = this.vacancy;
      try {
        // пришла ссылка вида //youtube.com/embed/videoid
        if (!src.startsWith('http')) src = `https:${src}`;
        const url = new URL(src);
        const isYoutubeEmbed = ['www.youtube.com', 'youtube.com'].includes(
          url.hostname,
        );
        if (!isYoutubeEmbed) return null;
        const [, videoid] = url.pathname.split('/embed/');
        return {
          videoid,
          playlabel: title,
        };
      } catch (error) {
        return null;
      }
    },
  },
};
</script>
